import React, { useState, useEffect, useRef } from 'react';
import { MapPin, Phone, Mail, Calendar, Check, Loader2, ArrowRight, FileCheck } from 'lucide-react';

const INQUIRY_TYPES = [
  'Forhandling & B2B',
  'Smagning & Besøg',
  'Specialaftapning',
  'Generel Henvendelse'
];

export default function Contact() {
  const [contactInfo, setContactInfo] = useState({
    kontakt_adresse: 'Holtvej 8, 6470 Sydals, Danmark',
    kontakt_telefon: '+45 30 26 56 11',
    kontakt_aabningstider: 'Mandag – Fredag: 09.00 – 16.00',
    kontakt_email: 'kmu@destillerietals.dk',
    kontakt_find_smiley_url: 'https://www.findsmiley.dk/app/758730'
  });

  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    inquiry_type: 'Forhandling & B2B',
    message: '',
    website_url: '' // Honeypot field (strictly hidden)
  });

  const [copiedEmail, setCopiedEmail] = useState(false);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [serverError, setServerError] = useState('');
  const [isInView, setIsInView] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    async function fetchContactInfo() {
      try {
        const res = await fetch('/_api/public/v2/content/forside');
        if (res.ok) {
          const json = await res.json();
          if (json?.items?.[0]) {
            setContactInfo((prev) => ({
              ...prev,
              ...json.items[0],
            }));
          }
        }
      } catch (err) {
        console.warn('Using fallback contact info:', err);
      }
    }
    fetchContactInfo();
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || !('IntersectionObserver' in window)) {
      setIsInView(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      {
        threshold: 0.1,
        rootMargin: '0px 0px -30px 0px',
      }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const handleCopyEmail = () => {
    navigator.clipboard?.writeText(contactInfo.kontakt_email || 'kmu@destillerietals.dk');
    setCopiedEmail(true);
    setTimeout(() => setCopiedEmail(false), 2500);
  };

  const validate = () => {
    const errs = {};
    if (!formData.name.trim()) {
      errs.name = 'Venligst indtast dit navn';
    }
    if (!formData.email.trim()) {
      errs.email = 'Venligst indtast din e-mail';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      errs.email = 'Ugyldig e-mailadresse';
    }
    if (!formData.message.trim()) {
      errs.message = 'Venligst skriv din besked';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleTypeSelect = (type) => {
    setFormData((prev) => ({ ...prev, inquiry_type: type }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setServerError('');

    if (!validate()) return;

    setIsSubmitting(true);
    try {
      const response = await fetch('/_api/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setIsSubmitted(true);
        setFormData({
          name: '',
          company: '',
          email: '',
          phone: '',
          inquiry_type: 'Forhandling & B2B',
          message: '',
          website_url: ''
        });
      } else {
        const errorData = await response.json().catch(() => ({}));
        setServerError(
          errorData.message || 'Der opstod en fejl ved afsendelse. Prøv venligst igen.'
        );
      }
    } catch (err) {
      console.error('Submission error:', err);
      setServerError('Kunne ikke forbinde til serveren. Tjek din internetforbindelse.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      className="relative w-full bg-[#F6F2EB] py-28 md:py-36 px-6 md:px-12 text-[#37332F] overflow-hidden scroll-mt-20"
      aria-label="Kontakt KALAS"
    >
      {/* Background Watermark Typography */}
      <div className="absolute -top-4 sm:top-0 left-1/2 -translate-x-1/2 font-['Cormorant_Garamond'] text-6xl sm:text-[12rem] md:text-[16rem] lg:text-[18rem] font-light text-[#37332F]/[0.03] uppercase tracking-[0.25em] select-none pointer-events-none z-0 leading-none">
        KALAS
      </div>

      {/* Atmospheric Subtle Warm Glows */}
      <div className="absolute top-1/4 -right-32 w-96 h-96 bg-[#DED7CC]/30 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-10 -left-32 w-96 h-96 bg-[#C6D3D4]/20 rounded-full blur-3xl pointer-events-none" />

      <div className="mx-auto max-w-7xl relative z-10">
        
        {/* Section Top Header */}
        <div className="mb-16 md:mb-20 flex flex-col md:flex-row md:items-end justify-between gap-6 border-b border-[#DED7CC] pb-8">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <span className="w-2 h-2 bg-[#C99850]" />
              <span className="font-['Montserrat'] text-xs uppercase tracking-[0.3em] text-[#C99850]">
                Ræk Ud Til Os · Destilleriet Als
              </span>
            </div>
            <h2 className="font-['Cormorant_Garamond'] text-4xl sm:text-6xl md:text-7xl font-light uppercase tracking-[0.2em] text-[#37332F] leading-none break-words">
              Kontakt Os
            </h2>
          </div>
          <p className="text-xs sm:text-sm text-[#5E5852] font-light max-w-md leading-relaxed">
            Vi byder velkommen til henvendelser vedrørende forhandling, B2B, smagninger og private rundvisninger på Sydals.
          </p>
        </div>

        {/* Main Grid */}
        <div
          ref={containerRef}
          className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start"
        >
          {/* Left Column: Direct Contact & Info Cards */}
          <div
            className={`lg:col-span-5 space-y-6 transition-all duration-1000 ease-out transform ${
              isInView
                ? 'opacity-100 translate-x-0'
                : 'opacity-0 -translate-x-10 pointer-events-none'
            }`}
          >
            {/* Quick Summary Card */}
            <div className="p-6 sm:p-8 bg-[#F2EEE7] border border-[#DED7CC] space-y-6 shadow-xs">
              
              {/* Address */}
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-[#F6F2EB] border border-[#DED7CC] flex items-center justify-center text-[#C99850] shrink-0 mt-0.5">
                  <MapPin className="w-4 h-4" aria-hidden="true" />
                </div>
                <div className="space-y-1">
                  <p className="font-['Cormorant_Garamond'] text-lg sm:text-xl font-normal text-[#37332F] uppercase tracking-wider">
                    KALAS Distillery
                  </p>
                  <p className="text-xs sm:text-sm text-[#5E5852] font-light">
                    {contactInfo.kontakt_adresse}
                  </p>
                </div>
              </div>

              <div className="h-px bg-[#DED7CC] w-full" />

              {/* Phone */}
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-[#F6F2EB] border border-[#DED7CC] flex items-center justify-center text-[#C99850] shrink-0 mt-0.5">
                  <Phone className="w-4 h-4" aria-hidden="true" />
                </div>
                <div className="space-y-1">
                  <a
                    href={`tel:${contactInfo.kontakt_telefon.replace(/\s+/g, '')}`}
                    className="font-['Montserrat'] text-base sm:text-lg font-medium text-[#37332F] hover:text-[#C99850] transition-colors block"
                  >
                    {contactInfo.kontakt_telefon}
                  </a>
                  <span className="text-[11px] text-[#8E867E] font-light block">
                    {contactInfo.kontakt_aabningstider}
                  </span>
                </div>
              </div>

              <div className="h-px bg-[#DED7CC] w-full" />

              {/* Email */}
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-[#F6F2EB] border border-[#DED7CC] flex items-center justify-center text-[#C99850] shrink-0 mt-0.5">
                  <Mail className="w-4 h-4" aria-hidden="true" />
                </div>
                <div className="space-y-1 flex-1">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <a
                      href={`mailto:${contactInfo.kontakt_email}`}
                      className="font-['Montserrat'] text-xs sm:text-sm text-[#37332F] hover:text-[#C99850] transition-colors"
                    >
                      {contactInfo.kontakt_email}
                    </a>
                    <button
                      type="button"
                      onClick={handleCopyEmail}
                      className="text-[10px] font-['Montserrat'] uppercase tracking-wider px-2.5 py-1 bg-[#F6F2EB] border border-[#DED7CC] text-[#5E5852] hover:text-[#37332F] hover:border-[#37332F] transition-colors cursor-pointer"
                    >
                      {copiedEmail ? 'Kopieret!' : 'Kopiér'}
                    </button>
                  </div>
                </div>
              </div>

              <div className="h-px bg-[#DED7CC] w-full" />

              {/* Kontrolrapport / Find Smiley */}
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-[#F6F2EB] border border-[#DED7CC] flex items-center justify-center text-[#C99850] shrink-0 mt-0.5">
                  <FileCheck className="w-4 h-4" aria-hidden="true" />
                </div>
                <div className="space-y-1">
                  <a
                    href={contactInfo.kontakt_find_smiley_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-['Montserrat'] text-xs sm:text-sm font-medium text-[#37332F] hover:text-[#C99850] transition-colors inline-flex items-center gap-1.5 group"
                  >
                    <span>Fødevarestyrelsens kontrolrapport</span>
                    <span className="text-[10px] text-[#C99850] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform">↗</span>
                  </a>
                  <span className="text-[11px] text-[#8E867E] font-light block">
                    Find Smiley status & tilsyn
                  </span>
                </div>
              </div>

            </div>

            {/* Visit & Tour Badge */}
            <div className="p-5 sm:p-6 bg-[#C6D3D4]/20 border border-[#C6D3D4]/60 flex items-start gap-4">
              <div className="w-9 h-9 bg-[#F6F2EB] border border-[#C6D3D4] flex items-center justify-center text-[#37332F] shrink-0">
                <Calendar className="w-4 h-4" aria-hidden="true" />
              </div>
              <div className="space-y-1">
                <h4 className="font-['Cormorant_Garamond'] text-base font-normal uppercase tracking-wider text-[#37332F]">
                  Rundvisning & Smagninger
                </h4>
                <p className="text-xs text-[#5E5852] font-light leading-relaxed">
                  Destilleriet er åbent for private rundvisninger og faglige masterclasses efter forudgående aftale.
                </p>
              </div>
            </div>

            {/* Micro Badges */}
            <div className="flex items-center gap-3 pt-2 text-[11px] font-['Montserrat'] uppercase tracking-wider text-[#8E867E]">
              <span className="px-3 py-1.5 bg-[#F2EEE7] border border-[#DED7CC] flex items-center gap-2">
                <span className="w-1.5 h-1.5 bg-[#C99850]" />
                Hurtig svartid
              </span>
              <span className="px-3 py-1.5 bg-[#F2EEE7] border border-[#DED7CC]">
                Sydals, Danmark
              </span>
            </div>
          </div>

          {/* Right Column: Contact Form */}
          <div
            className={`lg:col-span-7 bg-[#F2EEE7] border border-[#DED7CC] p-6 sm:p-10 md:p-12 shadow-sm relative transition-all duration-1000 ease-out delay-150 transform ${
              isInView
                ? 'opacity-100 translate-x-0'
                : 'opacity-0 translate-x-10 pointer-events-none'
            }`}
          >
            <div className="mb-6">
              <h3 className="font-['Cormorant_Garamond'] text-2xl sm:text-3xl font-light uppercase tracking-[0.15em] text-[#37332F]">
                Send En Henvendelse
              </h3>
            </div>

            {isSubmitted ? (
              <div className="py-12 px-6 border border-[#C99850]/50 bg-[#F6F2EB] text-center space-y-4">
                <div className="w-12 h-12 border border-[#C99850] text-[#C99850] flex items-center justify-center mx-auto text-xl bg-[#F2EEE7]">
                  <Check className="w-6 h-6" aria-hidden="true" />
                </div>
                <h4 className="font-['Cormorant_Garamond'] text-2xl font-light text-[#37332F] uppercase tracking-wider">
                  Mange tak for din besked
                </h4>
                <p className="text-xs sm:text-sm text-[#5E5852] font-light leading-relaxed max-w-md mx-auto">
                  Vi har modtaget din henvendelse og vender tilbage til dig hurtigst muligt.
                </p>
                <div className="pt-4">
                  <button
                    type="button"
                    onClick={() => setIsSubmitted(false)}
                    className="px-6 py-2.5 text-xs font-['Montserrat'] font-medium uppercase tracking-widest text-[#F6F2EB] bg-[#37332F] hover:bg-[#2B2724] transition-colors cursor-pointer"
                  >
                    Send endnu en besked
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} noValidate className="space-y-5">
                {/* Honeypot Spam Protection Field (Strictly hidden from humans) */}
                <input
                  type="text"
                  name="website_url"
                  value={formData.website_url}
                  onChange={handleChange}
                  tabIndex={-1}
                  autoComplete="off"
                  className="sr-only absolute -left-[9999px]"
                  aria-hidden="true"
                />

                {/* Name & Company */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label
                      htmlFor="contact-name"
                      className="block text-[11px] font-['Montserrat'] uppercase tracking-[0.2em] text-[#37332F] font-medium"
                    >
                      Navn <span className="text-[#C99850]">*</span>
                    </label>
                    <input
                      id="contact-name"
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Dit fulde navn"
                      required
                      className={`w-full px-4 py-3 bg-[#FBF9F5] border text-[#37332F] text-sm focus:outline-none focus:border-[#37332F] transition-colors placeholder:text-[#8E867E]/50 ${
                        errors.name ? 'border-red-500' : 'border-[#DED7CC]'
                      }`}
                    />
                    {errors.name && (
                      <p className="text-[11px] text-red-600 font-normal">{errors.name}</p>
                    )}
                  </div>

                  <div className="space-y-1.5">
                    <label
                      htmlFor="contact-company"
                      className="block text-[11px] font-['Montserrat'] uppercase tracking-[0.2em] text-[#37332F] font-medium"
                    >
                      Firma / Restaurant / Butik
                    </label>
                    <input
                      id="contact-company"
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      placeholder="Valgfrit"
                      className="w-full px-4 py-3 bg-[#FBF9F5] border border-[#DED7CC] text-[#37332F] text-sm focus:outline-none focus:border-[#37332F] transition-colors placeholder:text-[#8E867E]/50"
                    />
                  </div>
                </div>

                {/* Email & Phone */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label
                      htmlFor="contact-email"
                      className="block text-[11px] font-['Montserrat'] uppercase tracking-[0.2em] text-[#37332F] font-medium"
                    >
                      E-mail <span className="text-[#C99850]">*</span>
                    </label>
                    <input
                      id="contact-email"
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="din@email.dk"
                      required
                      className={`w-full px-4 py-3 bg-[#FBF9F5] border text-[#37332F] text-sm focus:outline-none focus:border-[#37332F] transition-colors placeholder:text-[#8E867E]/50 ${
                        errors.email ? 'border-red-500' : 'border-[#DED7CC]'
                      }`}
                    />
                    {errors.email && (
                      <p className="text-[11px] text-red-600 font-normal">{errors.email}</p>
                    )}
                  </div>

                  <div className="space-y-1.5">
                    <label
                      htmlFor="contact-phone"
                      className="block text-[11px] font-['Montserrat'] uppercase tracking-[0.2em] text-[#37332F] font-medium"
                    >
                      Telefon
                    </label>
                    <input
                      id="contact-phone"
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+45 12 34 56 78"
                      className="w-full px-4 py-3 bg-[#FBF9F5] border border-[#DED7CC] text-[#37332F] text-sm focus:outline-none focus:border-[#37332F] transition-colors placeholder:text-[#8E867E]/50"
                    />
                  </div>
                </div>

                {/* Message */}
                <div className="space-y-1.5">
                  <label
                    htmlFor="contact-message"
                    className="block text-[11px] font-['Montserrat'] uppercase tracking-[0.2em] text-[#37332F] font-medium"
                  >
                    Besked <span className="text-[#C99850]">*</span>
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    rows={4}
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Beskriv din henvendelse, forespørgsel eller ønsket antal flasker..."
                    required
                    className={`w-full px-4 py-3 bg-[#FBF9F5] border text-[#37332F] text-sm focus:outline-none focus:border-[#37332F] transition-colors placeholder:text-[#8E867E]/50 resize-none ${
                      errors.message ? 'border-red-500' : 'border-[#DED7CC]'
                    }`}
                  />
                  {errors.message && (
                    <p className="text-[11px] text-red-600 font-normal">{errors.message}</p>
                  )}
                </div>

                {serverError && (
                  <div className="p-3.5 bg-red-50 border border-red-300 text-red-700 text-xs">
                    {serverError}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-4 text-xs font-['Montserrat'] font-medium uppercase tracking-[0.25em] text-[#F6F2EB] bg-[#37332F] hover:bg-[#201D1A] border border-[#37332F] transition-colors flex items-center justify-center gap-3 disabled:opacity-50 cursor-pointer shadow-sm"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
                      <span>Sender henvendelse...</span>
                    </>
                  ) : (
                    <>
                      <span>Send henvendelse</span>
                      <ArrowRight className="w-3.5 h-3.5" aria-hidden="true" />
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
